import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Kpi, PctDelta, UP, DOWN } from '../rankings/parts';
import { fleetWeek, siteMovers, weekWindows, weekWindowsEndingAt, siteName, fmtUsd } from './doNext';

/* "Last 7 days" at the top of the console home: the few numbers that say
   whether the fleet is growing (views, AdSense, RPM against the week
   before), messages waiting for a reply, and which sites moved most. */

const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US');

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortDate = (iso) => {
  const [, m, d] = String(iso || '').split('-').map(Number);
  return m && d ? `${SHORT[m - 1]} ${d}` : '';
};
const range = (days) => (days && days.length ? `${shortDate(days[0])}–${shortDate(days[days.length - 1])}` : '');

const sub = (cur, prev, note) => (
  <>
    <PctDelta cur={cur} prev={prev} /> <span className="muted">{note}</span>
  </>
);

function MoverList({ title, rows, color, onSelect }) {
  return (
    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
      <div className="kpi-label">{title}</div>
      {rows.length === 0 ? (
        <div className="muted small">None this week.</div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {rows.map((r) => (
            <li key={r.domain.id} className="small" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '3px 0' }}>
              <Link
                to={`/domain/${encodeURIComponent(r.domain.id)}`}
                onClick={() => onSelect && onSelect(r.domain)}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {siteName(r.domain, r.domain.id)}
              </Link>
              <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ color, fontWeight: 600 }}>{r.delta > 0 ? '+' : '−'}{fmtInt(Math.abs(r.delta))}</span>
                <span className="muted"> views{r.change != null ? ` (${r.delta > 0 ? '+' : '−'}${Math.round(Math.abs(r.change) * 100)}%)` : ''}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* messages: the inbox alert summary, null while loading, { error } when it failed. */
export default function WeekCard({ domains, metrics, ready, adsenseDaily, messages, onSelectDomain }) {
  const viewWindows = useMemo(() => weekWindows(), []);
  const earnWindows = useMemo(
    () => (adsenseDaily && !adsenseDaily.error ? weekWindowsEndingAt(adsenseDaily.end) : null),
    [adsenseDaily]);
  const week = useMemo(
    () => (ready ? fleetWeek(domains, metrics, viewWindows, earnWindows) : null),
    [ready, domains, metrics, viewWindows, earnWindows]);
  const movers = useMemo(
    () => (ready ? siteMovers(domains, metrics, viewWindows) : { up: [], down: [] }),
    [ready, domains, metrics, viewWindows]);

  const waiting = messages && !messages.error ? (messages.unhandled || 0) : null;
  const feedbackFirst = messages && messages.by_type && messages.by_type.page_feedback;
  const earnLag = earnWindows && earnWindows.recent[6] !== viewWindows.recent[6];

  return (
    <section className="card" style={{ marginBottom: '20px' }} aria-label="Last 7 days">
      <div className="card-header" style={{ flexWrap: 'wrap', gap: '6px' }}>
        <h2 className="card-title">Last 7 days</h2>
        <span className="muted small">{range(viewWindows.recent)} against the 7 days before</span>
      </div>

      <div className="kpi-grid" style={{ marginBottom: '12px' }}>
        <Kpi
          label={metrics.trafficDaily ? 'Human views' : 'Page views'}
          value={week ? fmtInt(week.views.recent) : '…'}
          sub={week ? sub(week.views.recent, week.views.prev,
            metrics.trafficDaily
              ? `of ${fmtInt(week.allViews.recent)} views (${fmtInt(week.allViews.recent - week.views.recent)} unverified)`
              : `vs ${fmtInt(week.views.prev)}`) : null}
          hint={metrics.trafficDaily
            ? `All sites, complete MST days. ${metrics.humanRule || ''}`
            : 'All sites, complete MST days'}
        />
        <Kpi
          label="AdSense"
          value={!week ? '…' : week.earnings ? fmtUsd(week.earnings.recent) : '—'}
          sub={week && week.earnings
            ? sub(week.earnings.recent, week.earnings.prev,
              `vs ${fmtUsd(week.earnings.prev)}${earnLag ? ` · to ${shortDate(earnWindows.recent[6])}` : ''}`)
            : week ? <span className="muted">no daily AdSense data</span> : null}
          hint={earnWindows
            ? `AdSense estimated earnings, ${range(earnWindows.recent)} (the latest 7 days AdSense has reported)`
            : 'AdSense daily earnings have not been loaded'}
        />
        <Kpi
          label="RPM"
          value={week && week.rpm && week.rpm.recent != null ? fmtUsd(week.rpm.recent) : week ? '—' : '…'}
          sub={week && week.rpm && week.rpm.recent != null ? sub(week.rpm.recent, week.rpm.prev, 'per 1,000 views') : null}
          hint="AdSense earnings per 1,000 page views (all views, human or not: AdSense pays for every one), both counted over the AdSense days"
        />
        {waiting == null ? (
          <Kpi label="Messages waiting" value={messages ? '—' : '…'} sub={messages ? <span className="muted">could not be loaded</span> : null} />
        ) : (
          <Link
            to={`/emails?filter=${feedbackFirst ? 'feedback' : 'contact'}`}
            className="kpi"
            style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
            title="Reader feedback and contact form messages not yet marked handled"
          >
            <div className="kpi-label">Messages waiting</div>
            <div className="kpi-value" style={waiting > 0 ? { color: '#92400e' } : undefined}>{fmtInt(waiting)}</div>
            <div className="kpi-sub">{waiting > 0 ? 'open the inbox' : 'all answered'}</div>
          </Link>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 32px' }}>
        <MoverList title="Biggest gains" rows={movers.up} color={UP} onSelect={onSelectDomain} />
        <MoverList title="Biggest losses" rows={movers.down} color={DOWN} onSelect={onSelectDomain} />
      </div>
      <div className="muted small" style={{ marginTop: '8px' }}>
        Gains and losses compare each site’s last 7 days of {metrics.trafficDaily ? 'probably-human ' : ''}views with
        its typical week (7 × its median day over the 3 weeks before), so a bot burst of a few days does not count.
        Parked sites are left out.
      </div>
    </section>
  );
}
